import { type Action, type ThunkDispatch } from '@reduxjs/toolkit';

import {
    type ApiResult,
    type EmojiId,
    Emojis,
    type EmojiVisualizerFiles,
    type EmojiVisualizerIsLocked,
    type EmojiVisualizerMetadata,
    type EmojiVisualizerName,
    type EmojiVisualizerNameIsLocked,
    type ExtendedEmoji,
    type SearchResponse,
    type Tag,
} from '@app/client';
import { apiFactory } from '@app/client/apiFactory/api';

import { type RootState } from '../reducers';
import { LOCAL_STORAGE_CURRENT_EMOJI_KEY } from '../reducers/user-emojis';
import {
    ACTION_SET_CURRENT_EMOJI_TAGS,
    ACTION_SET_CURRENT_EMOJI_VISUALIZER_FILES,
    ACTION_SET_CURRENT_EMOJI_VISUALIZER_METADATA,
    ACTION_SET_CURRENT_EMOJI_VISUALIZER_NAME,
    ACTION_SET_CURRENT_EMOJI_VISUALIZER_URL,
    ACTION_SET_CURRENT_USER_EMOJI,
    ACTION_SET_CURRENT_USER_EMOJI_SEARCH,
    ACTION_SET_LOADING_EMOJIS,
    ACTION_SET_USER_EMOJIS,
    ACTION_SET_USER_EXTENDED_EMOJIS,
    ACTION_SET_USER_REMOVED_EMOJIS,
    type UserEmojiActionTypes,
} from '../types/user-emojis';

import { getDashboard } from './dashboard';
import { getAllVisualizerData } from './emoji';
import { setError } from './error';
import { loadCurrentEmojiOpenSeaUrl, setNftMintPanelStep } from './nft';

export const setUserEmojis = (emojis: EmojiId[]): UserEmojiActionTypes => ({
    type: ACTION_SET_USER_EMOJIS,
    emojis,
});

export const setUserExtendedEmojis = (emojis: ExtendedEmoji[]): UserEmojiActionTypes => ({
    type: ACTION_SET_USER_EXTENDED_EMOJIS,
    emojis,
});

export const setCurrentUserEmoji = (currentEmoji: EmojiId): UserEmojiActionTypes => ({
    type: ACTION_SET_CURRENT_USER_EMOJI,
    currentEmoji,
});

export const setCurrentUserEmojiSearch = (currentEmojiSearchResponse: SearchResponse): UserEmojiActionTypes => ({
    type: ACTION_SET_CURRENT_USER_EMOJI_SEARCH,
    currentEmojiSearchResponse,
});

export const setRemovedUserEmojis = (removedEmojis: EmojiId[]): UserEmojiActionTypes => ({
    type: ACTION_SET_USER_REMOVED_EMOJIS,
    removedEmojis,
});

export const searchEmojis = (eid: EmojiId, signal?: AbortSignal) => {
    return async function (
        dispatch?: ThunkDispatch<RootState, void, Action>,
    ): Promise<ApiResult<SearchResponse> | void> {
        return apiFactory()
            .call(Emojis.Emoji.search, { query: { emoji_id: eid }, settings: { signal } })
            .then((r) => {
                if (r?.ok && dispatch) {
                    dispatch(setCurrentUserEmojiSearch({ ...r.value }));
                }
                return r;
            })
            .catch((e) => {
                setError({
                    ...e,
                    log: true,
                    source: 'Error searching emojis',
                })();
                return e;
            });
    };
};

export function setActiveEmojiId(emojiId: EmojiId | null, emojis?: ExtendedEmoji[]) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>, getState: () => RootState): Promise<void> {
        const userEmojis = emojis || getState().userEmojis.extendedEmojis;
        let activeEmojiId =
            emojiId ||
            getState().userEmojis.currentEmoji ||
            localStorage.getItem(LOCAL_STORAGE_CURRENT_EMOJI_KEY) ||
            '';
        await dispatch(searchEmojis(activeEmojiId)); //fetches the rhythm score copy for current emoji
        if (activeEmojiId) {
            dispatch(setCurrentUserEmoji(activeEmojiId));
            // Reset NFT mint panel
            dispatch(setNftMintPanelStep(null));
            dispatch(setCurrentEmojiVisualizerFiles(null));
            dispatch(setCurrentEmojiVisualizerName(null, null));
            dispatch(setCurrentEmojiVisualizerMetadata(null, null));
            // Load visualizer info
            await dispatch(setCurrentUserEmojiVisualizerData(activeEmojiId));
            // Set Visualizer & OpenSea URL
            await dispatch(loadCurrentEmojiOpenSeaUrl());
            dispatch(setCurrentEmojiVisualizerUrl(activeEmojiId));
        }

        // Yat doesn't belong to user, get the first available
        const userOwnsYat = !!userEmojis.find((e) => e.display_format === activeEmojiId);
        if (!userOwnsYat) {
            activeEmojiId = userEmojis.length > 0 ? userEmojis[0].display_format : '';
        }
    };
}

export const setCurrentEmojiTags = (tags: Tag[]): UserEmojiActionTypes => ({
    type: ACTION_SET_CURRENT_EMOJI_TAGS,
    tags,
});

export const setLoadingEmojis = (loadingEmojis: boolean): UserEmojiActionTypes => ({
    type: ACTION_SET_LOADING_EMOJIS,
    loadingEmojis,
});

export const setCurrentEmojiVisualizerName = (
    visualizerName: EmojiVisualizerName | null,
    visualizerNameIsLocked?: EmojiVisualizerNameIsLocked,
): UserEmojiActionTypes => ({
    type: ACTION_SET_CURRENT_EMOJI_VISUALIZER_NAME,
    visualizerName,
    visualizerNameIsLocked,
});

export const setCurrentEmojiVisualizerFiles = (visualizerFiles: EmojiVisualizerFiles | null): UserEmojiActionTypes => ({
    type: ACTION_SET_CURRENT_EMOJI_VISUALIZER_FILES,
    visualizerFiles,
});

export const setCurrentEmojiVisualizerMetadata = (
    visualizerMetadata: EmojiVisualizerMetadata | null,
    visualizerIsLocked?: EmojiVisualizerIsLocked,
): UserEmojiActionTypes => ({
    type: ACTION_SET_CURRENT_EMOJI_VISUALIZER_METADATA,
    visualizerMetadata,
    visualizerIsLocked,
});

export const setCurrentEmojiVisualizerUrl = (currentEmoji: EmojiId): UserEmojiActionTypes => ({
    type: ACTION_SET_CURRENT_EMOJI_VISUALIZER_URL,
    currentEmoji,
});

export function getCurrentUserEmojis(showLoader = true) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<boolean> {
        try {
            if (showLoader) {
                dispatch(setLoadingEmojis(true));
            }
            const r = await apiFactory().call(Emojis.Emoji.getExtendedCollection, {});
            if (r.ok) {
                dispatch(setUserExtendedEmojis(r.value));
                dispatch(getDashboard());
                dispatch(notifyEmojisRemoved());
            } else {
                dispatch(setError({ ...r, source: 'UserEmojis - getCurrentUserEmojis()' }));
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            dispatch(setError({ ...e, source: 'UserEmojis - getCurrentUserEmojis() - catch' }));
        } finally {
            if (showLoader) {
                dispatch(setLoadingEmojis(false));
            }
        }
        return true;
    };
}

export function setCurrentUserEmojiVisualizerData(currentEmoji: EmojiId) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        try {
            if (currentEmoji) {
                const r = await getAllVisualizerData(currentEmoji);
                if (r?.ok && r?.value) {
                    const data = r.value;

                    const {
                        VisualizerData = { data: null, is_locked: false },
                        VisualizerFileLocations = { data: null, is_locked: false },
                        VisualizerName = { data: { name: null }, is_locked: false },
                    } = data;

                    if (VisualizerData) {
                        dispatch(
                            setCurrentEmojiVisualizerMetadata(
                                VisualizerData.data && Object.keys(VisualizerData.data).length > 0
                                    ? VisualizerData.data
                                    : null,
                                VisualizerData.is_locked,
                            ),
                        );
                    }
                    if (VisualizerFileLocations && VisualizerFileLocations.data) {
                        dispatch(
                            setCurrentEmojiVisualizerFiles({
                                image: VisualizerFileLocations.data.image,
                                video: VisualizerFileLocations.data.video,
                            }),
                        );
                    }
                    if (VisualizerName) {
                        dispatch(setCurrentEmojiVisualizerName(VisualizerName.data.name, VisualizerName.is_locked));
                    }
                }
            } else {
                dispatch(setCurrentEmojiVisualizerFiles(null));
                dispatch(setCurrentEmojiVisualizerMetadata(null, null));
                dispatch(setCurrentEmojiVisualizerName(null, null));
            }
        } catch (e) {
            dispatch(setCurrentEmojiVisualizerFiles(null));
            dispatch(setCurrentEmojiVisualizerMetadata(null, null));
            dispatch(setCurrentEmojiVisualizerName(null, null));
        }
    };
}

export function notifyEmojisRemoved(emojis?: ExtendedEmoji[]) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>, getState: () => RootState): Promise<void> {
        // Get previous list of emoji ids
        const previousEmojiIdsSerialized = localStorage.getItem('emojiIds');
        let previousEmojiIds = [];
        if (previousEmojiIdsSerialized) {
            previousEmojiIds = JSON.parse(previousEmojiIdsSerialized);
        }

        // Fetch new list of emoji ids
        const extendedEmojis = emojis || getState().userEmojis.extendedEmojis;
        let emojiList: string[] = [];
        if (extendedEmojis) {
            emojiList = extendedEmojis.map((emoji) => emoji.canonical_format);
        }

        // Calculate emojis that were removed from the account
        const difference = previousEmojiIds.filter((x) => !emojiList.includes(x));

        // If Yats removed
        if (difference.length > 0) {
            dispatch(setRemovedUserEmojis(difference));
        }

        // Save new list
        localStorage.setItem('emojiIds', JSON.stringify(emojiList));
    };
}
